import React, { useState } from 'react';
import { Folder, Plus, X, Edit2, Trash2 } from 'lucide-react';
import type { Folder as FolderType } from '../../hooks/useNotes';

interface FolderManagerProps {
  folders: FolderType[];
  onAddFolder: (name: string, color?: string) => Promise<void>;
  onUpdateFolder: (id: string, name: string, color?: string) => Promise<void>;
  onDeleteFolder: (id: string) => Promise<void>;
  onClose: () => void;
}

export function FolderManager({
  folders,
  onAddFolder,
  onUpdateFolder,
  onDeleteFolder,
  onClose,
}: FolderManagerProps) {
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#9FFF32');
  const [editingFolder, setEditingFolder] = useState<FolderType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingFolder) {
        await onUpdateFolder(editingFolder.id, newFolderName, newFolderColor);
        setEditingFolder(null);
      } else {
        await onAddFolder(newFolderName, newFolderColor);
      }
      setNewFolderName('');
    } catch (err) {
      setError('Failed to save folder. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this folder? Notes in this folder will be moved to root.')) {
      try {
        await onDeleteFolder(id);
      } catch (err) {
        setError('Failed to delete folder. Please try again.');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl">Manage Brain Dump Folders</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-500 p-3 rounded-xl mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <div className="flex gap-2">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Brain Dump folder name"
              className="flex-1 px-4 py-2 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
            />
            <input
              type="color"
              value={newFolderColor}
              onChange={(e) => setNewFolderColor(e.target.value)}
              className="w-12 h-10 rounded-xl cursor-pointer"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-[var(--accent-color)] text-black rounded-xl hover:opacity-90 transition-opacity"
            >
              {editingFolder ? 'Update' : 'Add'}
            </button>
          </div>
        </form>

        <div className="space-y-2">
          {folders.map((folder) => (
            <div
              key={folder.id}
              className="flex items-center justify-between p-3 bg-black/20 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: folder.color || '#9FFF32' }}
                >
                  <Folder className="w-4 h-4 text-black" />
                </div>
                <span>{folder.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setEditingFolder(folder);
                    setNewFolderName(folder.name);
                    setNewFolderColor(folder.color || '#9FFF32');
                  }}
                  className="p-2 hover:bg-black/20 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(folder.id)}
                  className="p-2 hover:bg-black/20 rounded-lg transition-colors text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}